import { useEffect, useRef, memo, useMemo } from "react";
import * as echarts from "echarts";

// Paletas de cor distintas para cada mapa (claro → escuro)
const PALETTES = [
  { colors: ["#dbeafe", "#93c5fd", "#2563eb", "#1e3a8a"], accent: "#1e3a8a", label: "#1e3a8a" }, // Azul
  { colors: ["#d1fae5", "#6ee7b7", "#059669", "#064e3b"], accent: "#064e3b", label: "#064e3b" }, // Verde
  { colors: ["#fef3c7", "#fcd34d", "#d97706", "#78350f"], accent: "#78350f", label: "#78350f" }, // Âmbar
];

function GeoMapCard({ label, geojson, data, total, paletteIndex = 0, situacao = "TODOS" }) {
  const chartRef = useRef(null);
  const mapName = `comp-geo-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const palette = PALETTES[paletteIndex % PALETTES.length];

  // Máximo de casos dentro deste mapa (para escala interna)
  const maxInterno = useMemo(
    () => Math.max(...(data ?? []).map((d) => d.value ?? 0), 1),
    [data]
  );

  useEffect(() => {
    if (!chartRef.current || !geojson?.features?.length) return;

    echarts.registerMap(mapName, geojson);

    const chart = echarts.init(chartRef.current, null, { renderer: "canvas" });

    chart.setOption({
      backgroundColor: "transparent",

      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(20,20,20,0.85)",
        borderColor: palette.accent,
        borderWidth: 1.5,
        textStyle: { color: "#fff", fontSize: 12 },
        formatter: (params) => {
          const d = params.data ?? {};
          const val = d.value ?? 0;
          const casosStr = val.toLocaleString("pt-BR");
          let metrica = "";
          if (situacao === "ABANDONO") {
            const taxa = d.taxa;
            metrica =
              taxa != null
                ? `Taxa de interrupção: <b>${taxa.toLocaleString("pt-BR")}%</b>`
                : "";
          } else {
            const inc = d.incidencia;
            metrica =
              inc != null
                ? `Incidência: <b>${inc.toLocaleString("pt-BR")} / 100 mil hab.</b>`
                : "";
          }
          return (
            `<b style="font-size:13px">${params.name}</b><br/>` +
            `Casos: <b>${casosStr}</b>` +
            (metrica ? `<br/>${metrica}` : "")
          );
        },
      },

      visualMap: {
        show: false,
        type: "continuous",
        min: 0,
        max: maxInterno,
        inRange: { color: palette.colors },
      },

      series: [
        {
          type: "map",
          map: mapName,
          roam: false,
          label: { show: false },
          data,

          // Áreas sem dados → cinza claro
          itemStyle: {
            areaColor: "#e5e7eb",
            borderColor: "#111",
            borderWidth: 1.2,
          },

          // Hover: borda mais grossa, cor de acento
          emphasis: {
            disabled: false,
            label: { show: false },
            itemStyle: {
              areaColor: palette.colors[2],
              borderColor: "#000",
              borderWidth: 2.5,
              shadowBlur: 6,
              shadowColor: "rgba(0,0,0,0.4)",
            },
          },

          select: { disabled: true },
        },
      ],
    });

    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(chartRef.current);

    return () => {
      observer.disconnect();
      chart.dispose();
    };
  }, [mapName, geojson, data, maxInterno, palette, situacao]);

  return (
    <div
      className="card h-100"
      style={{ borderTop: `3px solid ${palette.accent}`, overflow: "hidden" }}
    >
      {/* Cabeçalho */}
      <div
        className="card-header d-flex justify-content-between align-items-center"
        style={{ padding: "8px 14px", borderBottom: `1px solid ${palette.accent}22` }}
      >
        <span style={{ fontWeight: 700, fontSize: "0.92rem", color: palette.label }}>
          {label}
        </span>
        <span
          className="badge"
          style={{ background: palette.accent, fontSize: "0.75rem" }}
        >
          {(total ?? 0).toLocaleString("pt-BR")} casos
        </span>
      </div>

      {/* Mapa ECharts */}
      <div ref={chartRef} style={{ width: "100%", height: 300 }} />
    </div>
  );
}

export default memo(GeoMapCard);
